<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import SalesPartyDocumentTypeSelect from './SalesPartyDocumentTypeSelect.vue';
import SalesPartyDocumentValidityFields from './SalesPartyDocumentValidityFields.vue';
import { displayReferenceDate } from './postSaleDate';
import { salesPartyDocumentText } from './salesPartyDocumentI18n';

const props = defineProps({
  document: { type: Object, required: true },
  options: { type: Array, default: () => [] },
  canEdit: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  i18nScope: { type: String, default: 'SALES_PARTIES' },
  compact: { type: Boolean, default: false },
  showTypeSelector: { type: Boolean, default: true },
});

const emit = defineEmits(['save']);
const { t } = useI18n();
const documentTypes = ref([]);
const documentText = salesPartyDocumentText(t, props.i18nScope);

const typeEntries = value => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};
const cloneTypes = value =>
  typeEntries(value).map(item => ({
    id: String(item.id || item.tipo_documento_id || ''),
    name: item.name || item.nome || item.nome_tipo || '',
    validity_days: item.validity_days || item.validade_dias || '',
    issued_on: item.issued_on || item.data_emissao || '',
    expires_on: item.expires_on || item.data_vencimento || '',
  }));

watch(
  () => props.document.document_types,
  value => {
    documentTypes.value = cloneTypes(value);
  },
  { immediate: true, deep: true }
);

const selectedIds = computed(() => documentTypes.value.map(item => item.id));
const documentTypeOptions = computed(() =>
  props.options.map(option => ({
    value: String(option.id),
    label: option.name || option.nome,
  }))
);
const optionFor = id => props.options.find(option => String(option.id) === id);
const updateValidity = (index, item) => {
  documentTypes.value[index] = item;
};
const selectTypes = ids => {
  documentTypes.value = ids.map(id => {
    const current = documentTypes.value.find(item => item.id === id);
    if (current) return current;
    const option = optionFor(id) || {};
    return {
      id,
      name: option.name || option.nome || '',
      validity_days: option.validity_days || option.validade_dias || '',
      issued_on: '',
      expires_on: '',
    };
  });
};
const save = () =>
  emit('save', props.document, cloneTypes(documentTypes.value));
</script>

<template>
  <div
    :class="
      compact
        ? 'grid min-w-0 gap-[15px]'
        : 'grid gap-3 border-t border-lais-border-soft pt-3'
    "
  >
    <div
      v-if="compact"
      class="flex min-w-0 items-center max-[400px]:flex-wrap"
      data-test="compact-document-row"
    >
      <SalesPartyDocumentTypeSelect
        v-if="showTypeSelector"
        class="!w-[230px] shrink-0 max-[400px]:mb-[15px] max-[400px]:!w-full"
        :model-value="selectedIds"
        :options="documentTypeOptions"
        :placeholder="documentText.types"
        :multiple-label="documentText.typesSelected"
        :empty-state="documentText.typesEmpty"
        :disabled="!canEdit || saving || !options.length"
        data-test="compact-document-type-select"
        @update:model-value="selectTypes"
        @close="save"
      />
      <slot name="fileActions" />
    </div>

    <div v-else-if="!canEdit" class="flex flex-wrap gap-2">
      <span
        v-for="item in documentTypes"
        :key="item.id"
        class="rounded-full bg-lais-primary-50 px-2 py-0.5 text-[11px] font-semibold text-lais-primary"
      >
        {{ item.name }}
      </span>
      <span
        v-for="item in documentTypes.filter(item => item.validity_days)"
        :key="`${item.id}-validity`"
        class="rounded-full bg-lais-bg-soft px-2 py-0.5 text-[11px] text-lais-text-muted"
      >
        {{ item.validity_days }} {{ documentText.validityDays }}
        <span aria-hidden="true" class="px-1 before:content-['·']" />
        {{ displayReferenceDate(item.issued_on) || documentText.noIssuedOn }}
        <span aria-hidden="true" class="px-1 before:content-['·']" />
        {{ displayReferenceDate(item.expires_on) || documentText.noExpiresOn }}
      </span>
    </div>

    <template v-else-if="!compact">
      <LaisField
        :label="documentText.types"
        :helper="
          options.length ? documentText.typesHelper : documentText.typesEmpty
        "
        required
      >
        <SalesPartyDocumentTypeSelect
          :model-value="selectedIds"
          :options="documentTypeOptions"
          :placeholder="documentText.types"
          :multiple-label="documentText.typesSelected"
          :empty-state="documentText.typesEmpty"
          :disabled="saving || !options.length"
          @update:model-value="selectTypes"
          @close="save"
        />
      </LaisField>
    </template>

    <template v-for="(item, index) in documentTypes" :key="item.id">
      <SalesPartyDocumentValidityFields
        v-if="canEdit && item.validity_days"
        :i18n-scope="i18nScope"
        :item="item"
        @update="updateValidity(index, $event)"
        @save="save"
      />
    </template>
  </div>
</template>
